import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../styles/components/HealthTips.css";
import omnicareLogo from "../assets/icons/logo.svg";
import LanguageSwitcher from "./LanguageSwitcher";

const HealthTips = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState("all");

  const handleBackToLanding = () => navigate("/landing");

  const categories = [
    { id: "all", label: t("healthTips.categories.all"), icon: "📋" },
    { id: "nutrition", label: t("healthTips.categories.nutrition"), icon: "🥬" },
    { id: "activity", label: t("healthTips.categories.activity"), icon: "🏃" },
    { id: "screening", label: t("healthTips.categories.screening"), icon: "🔬" },
    { id: "substances", label: t("healthTips.categories.substances"), icon: "🚭" },
    { id: "environment", label: t("healthTips.categories.environment"), icon: "☀️" },
    { id: "sleep", label: t("healthTips.categories.sleep"), icon: "😴" },
    { id: "supplements", label: t("healthTips.categories.supplements"), icon: "💊" },
  ];

  const healthData = [
    // ─── NUTRITION & DIET ───
    { category: "nutrition", tipKey: "1", evidence: "IARC Monograph Vol. 114 (2015); WCRF/AICR Continuous Update Project 2018; Bouvard et al., Lancet Oncol 2015;16:1599-1600", impactLevel: "strong" },
    { category: "nutrition", tipKey: "2", evidence: "Aune et al., BMJ 2011;343:d6617, PMID: 22074852; WCRF/AICR 3rd Expert Report 2018", impactLevel: "strong" },
    { category: "nutrition", tipKey: "3", evidence: "WCRF/AICR CUP: Salt and salt-preserved foods → stomach cancer (convincing evidence); D'Elia et al., Cancer Epidemiol Biomarkers Prev 2012;21:640-648", impactLevel: "strong" },
    { category: "nutrition", tipKey: "4", evidence: "WCRF/AICR 2018; Aune et al., Int J Epidemiol 2017;46:1029-1056, PMID: 28338764 (95 meta-analyses, 142 publications)", impactLevel: "moderate" },
    { category: "nutrition", tipKey: "5", evidence: "Fiolet et al., BMJ 2018;360:k322, PMID: 29444771; Chang et al., eClinicalMedicine 2023;56:101840, PMID: 36691665", impactLevel: "moderate" },
    { category: "nutrition", tipKey: "6", evidence: "Lauby-Secretan et al., NEJM 2016;375:794-798; Chen et al., Nutr Cancer 2023;75:1051-1064, PMID: 37139871; NCI Obesity & Cancer Fact Sheet 2022", impactLevel: "strong" },
    // ─── PHYSICAL ACTIVITY ───
    { category: "activity", tipKey: "7", evidence: "WCRF/AICR 2018 (convincing for colon); Moore et al., JAMA Intern Med 2016;176:816-825, PMID: 27183032 (1.44M participants, 26 cancer types); Hermelink et al., Eur J Epidemiol 2022;37:447-460, PMID: 35612669", impactLevel: "strong" },
    { category: "activity", tipKey: "8", evidence: "Momma et al., Br J Sports Med 2022;56:755-763, PMID: 35228201; WHO Physical Activity Guidelines 2020", impactLevel: "moderate" },
    { category: "activity", tipKey: "9", evidence: "Hermelink et al., Eur J Epidemiol 2022;37:447-460, PMID: 35612669; Patterson et al., J Natl Cancer Inst 2018;110:1181-1189", impactLevel: "moderate" },
    // ─── SCREENING ───
    { category: "screening", tipKey: "10", evidence: "USPSTF 2021 (Grade A, 45-75; Grade C, 76-85); ACS 2018; Siegel et al., CA Cancer J Clin 2020;70:145-164 (early-onset CRC trends)", impactLevel: "strong" },
    { category: "screening", tipKey: "11", evidence: "USPSTF 2024 (Grade B, biennial 40-74); ACS 2015 (annual starting at 45, optional 40-44); Oeffinger et al., JAMA 2015;314:1599-1614", impactLevel: "strong" },
    { category: "screening", tipKey: "12", evidence: "USPSTF 2018 (Grade A); ACS 2020; CDC HPV Vaccination Recommendations 2024", impactLevel: "strong" },
    { category: "screening", tipKey: "13", evidence: "USPSTF 2021 (Grade B); National Lung Screening Trial, NEJM 2011;365:395-409; ACS 2023 updated criteria", impactLevel: "strong" },
    { category: "screening", tipKey: "14", evidence: "USPSTF 2018 (Grade C, 55-69); PCF Black Men Guidelines 2023; ACS 2023", impactLevel: "moderate" },
    { category: "screening", tipKey: "15", evidence: "USPSTF HCV 2020 (Grade B); USPSTF HBV 2020 (Grade B for at-risk); AASLD HCC Surveillance 2023; El-Serag, NEJM 2011;365:1118-1127", impactLevel: "strong" },
    // ─── TOBACCO & ALCOHOL ───
    { category: "substances", tipKey: "16", evidence: "US Surgeon General 2014; Zhang et al., Br J Cancer 2022;126:1637-1646, PMID: 35194190; CDC Smoking Cessation Guidelines 2020", impactLevel: "strong" },
    { category: "substances", tipKey: "17", evidence: "Jun et al., Epidemiol Health 2023;45:e2023092, PMID: 37905315; IARC Monograph Vol. 100E; ACS Alcohol & Cancer Guidelines; Ng et al., Ann Epidemiol 2023;85:113-120, PMID: 37268241", impactLevel: "strong" },
    { category: "substances", tipKey: "18", evidence: "EPA 1992 (classified as Group A carcinogen); US Surgeon General 2006; Wang et al., Chest 2024;165:1538-1554, PMID: 38253312", impactLevel: "strong" },
    // ─── ENVIRONMENT & SKIN ───
    { category: "environment", tipKey: "19", evidence: "AAD Skin Cancer Prevention Guidelines; WHO UV & Health Report; ACS 2024; Green et al., J Clin Oncol 2011;29:257-263 (sunscreen reduces melanoma by 50%)", impactLevel: "strong" },
    { category: "environment", tipKey: "20", evidence: "EPA Assessment of Risks from Radon 2003; NCI Radon & Cancer Fact Sheet; WHO Radon Handbook 2009", impactLevel: "moderate" },
    { category: "environment", tipKey: "21", evidence: "IARC Monographs Programme; OSHA Carcinogen Standards; Straif et al., Lancet Oncol 2009;10:1033-1034", impactLevel: "moderate" },
    // ─── SLEEP & STRESS ───
    { category: "sleep", tipKey: "22", evidence: "IARC Monograph Vol. 124 (2019); Erren et al., Dtsch Arztebl Int 2016;113:563-568; Rivera et al., Int J Cancer 2020;146:2052-2061", impactLevel: "moderate" },
    { category: "sleep", tipKey: "23", evidence: "Chida et al., Nat Clin Pract Oncol 2008;5:466-475; Antoni et al., Brain Behav Immun 2006;20:11-20; NCI Psychological Stress & Cancer Fact Sheet", impactLevel: "moderate" },
    // ─── SUPPLEMENTS ───
    { category: "supplements", tipKey: "24", evidence: "Manson et al., NEJM 2019;380:33-44 (VITAL trial); Keum et al., Ann Oncol 2019;30:733-743; IOM 2011 Dietary Reference Intakes", impactLevel: "moderate" },
    { category: "supplements", tipKey: "25", evidence: "Klein et al., JAMA 2011;306:1549-1556 (SELECT, PMID: 21990298); ATBC Cancer Prevention Study Group, NEJM 1994;330:1029-1035; USPSTF 2022 (Grade I for multivitamins for cancer prevention)", impactLevel: "strong" },
    { category: "supplements", tipKey: "26", evidence: "Rothwell et al., Lancet 2011;377:31-41; USPSTF 2022 (withdrew CRC prevention recommendation); Burn et al., Lancet 2020;395:1855-1863 (CAPP2 trial, Lynch syndrome)", impactLevel: "moderate" },
  ];

  const filteredData = activeCategory === "all"
    ? healthData
    : healthData.filter(item => item.category === activeCategory);

  const getImpactBadge = (level) => {
    switch (level) {
      case "strong": return { label: t("healthTips.strongEvidence"), className: "impact-strong" };
      case "moderate": return { label: t("healthTips.moderateEvidence"), className: "impact-moderate" };
      default: return { label: t("healthTips.emerging"), className: "impact-emerging" };
    }
  };

  return (
    <div className="health-tips-page">
      {/* Header */}
      <header className="health-tips-header">
        <div className="logo-section">
          <div className="logo" onClick={handleBackToLanding} style={{ cursor: "pointer" }}>
            <img src={omnicareLogo} alt="Pebble Health Logo" className="logo-icon" />
            <span className="logo-text">Pebble Health</span>
          </div>
        </div>
        <div className="header-actions">
          <LanguageSwitcher />
          <button className="back-button" onClick={handleBackToLanding}>
            {t("healthTips.back")}
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="health-tips-content">
        <div className="health-tips-header-section">
          <h1 className="main-title">{t("healthTips.pageTitle")}</h1>
          <p className="subtitle">
            {t("healthTips.pageSubtitle")}
          </p>
          <div className="evidence-note">
            <strong>{t("healthTips.howToRead")}</strong> {t("healthTips.howToReadBody")}
          </div>
        </div>

        {/* Category Filter */}
        <div className="category-filter">
          {categories.map(cat => (
            <button
              key={cat.id}
              className={`category-btn ${activeCategory === cat.id ? "active" : ""}`}
              onClick={() => setActiveCategory(cat.id)}
            >
              <span className="cat-icon">{cat.icon}</span>
              <span className="cat-label">{cat.label}</span>
            </button>
          ))}
        </div>

        {/* Tips Grid */}
        <div className="tips-grid">
          {filteredData.map((item, index) => {
            const impact = getImpactBadge(item.impactLevel);
            return (
              <div key={index} className="evidence-card">
                <div className="evidence-card-header">
                  <h3 className="evidence-title">{t(`healthTips.tips.${item.tipKey}.title`)}</h3>
                  <span className={`impact-badge ${impact.className}`}>
                    {impact.label}
                  </span>
                </div>
                <p className="evidence-detail">{t(`healthTips.tips.${item.tipKey}.detail`)}</p>
                <div className="evidence-action">
                  <strong>{t("healthTips.whatToDo")}</strong> {t(`healthTips.tips.${item.tipKey}.actionable`)}
                </div>
                <div className="evidence-source">
                  <span className="source-icon">📄</span>
                  <span className="source-text">{item.evidence}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer Disclaimer */}
        <div className="tips-disclaimer">
          <p>
            <strong>{t("healthTips.disclaimer")}</strong> {t("healthTips.disclaimerBody")}
          </p>
          <p className="last-updated">
            {t("healthTips.lastReviewed")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default HealthTips;
